/**
 * Unified TTS voice picker.
 *
 * One voice per language applies across Read Aloud, Ami, word-tap, and
 * flashcard practice. The picker has two render variants:
 *
 *  - Full picker: high quality + Browser sections. Shown when the user's tier
 *    allows high quality voices for the entry feature.
 *  - Simplified: single Browser list with an upgrade row for high quality
 *    voices at the end. Shown when they aren't usable for the entry feature
 *    (free in any context, or standard entering from Read Aloud).
 *
 * The simplified path writes via `setBrowserFallback`, which leaves any
 * existing global Azure pref intact — so a standard user picking a browser
 * voice from the Read Aloud picker doesn't lose their high quality voice for
 * word-tap / flashcards / chat.
 */

import { effect } from "@preact/signals";
import * as preact from "preact";

import * as modalController from "modalController";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import stores from "stores/stores";
import type { TTSFeature, VoiceOption, VoicePickerState } from "tts";
import * as tts from "tts";
import TTSUpsellModal from "views/TTSUpsellModal";
import { TTSRateSelector, TTSVoiceRow } from "views/TTSVoiceControls";

interface Props {
  language: string;
  /**
   * Feature that opened this picker. Drives the layout: a Read Aloud entry
   * for a Premium user gets the simplified browser-only view (since HD
   * isn't usable for Read Aloud at that tier), while a Speaking-mode entry
   * for the same user gets the full picker.
   */
  feature: TTSFeature;
  close: () => void;
}

interface State {
  pickerState?: VoicePickerState;
}

export class TTSVoicePickerView extends preact.Component<Props, State> {
  disposeUserEffect?: () => void;

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.loadVoices();
    // Re-load whenever the Azure voice pref changes (e.g. when a stacked
    // Speaking-mode picker on top of this one writes a new voice), or when
    // device voices arrive after the picker opened (Chrome/Edge Android PWAs
    // enumerate browser voices asynchronously) so the browser voice group fills
    // in without reopening the picker.
    this.disposeUserEffect = effect(() => {
      void stores.user.state.value.ttsVoices;
      void tts.voicesChanged.value;
      void this.loadVoices();
    });
  }

  componentWillUnmount() {
    this.disposeUserEffect?.();
  }

  loadVoices = async () => {
    await tts.initialize(this.props.language);
    this.setState({
      pickerState: tts.getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language: this.props.language,
      }),
    });
  };

  /**
   * Full-picker write — sets the voice for the entry feature. Auto-mirrors
   * to the other feature on first pick (handled inside setVoice).
   */
  onPickVoice = async (voice: VoiceOption) => {
    await tts.selectVoice({
      user: stores.user,
      language: this.props.language,
      voiceId: voice.id,
      feature: this.props.feature,
    });
    this.setState({
      pickerState: this.state.pickerState
        ? {
            ...this.state.pickerState,
            selectedVoiceId: voice.id,
            selectedVoiceName: voice.displayName,
            selectedVoiceSource: "stored",
          }
        : undefined,
    });
    this.playSelectedConfirmation(voice.id);
  };

  /**
   * Persists a simplified-picker browser pick. Same as the full picker's
   * onPickVoice but without the spoken confirmation — the Continue handler
   * calls this when committing the highlighted default voice on dismiss.
   * Returns the underlying promise so the Continue handler can await before
   * closing (avoids the race where close → playTTS runs before the legacy
   * Azure-pref clear server PATCH has completed).
   */
  persistBrowserFallback = async (voice: VoiceOption) => {
    await tts.selectVoice({
      user: stores.user,
      language: this.props.language,
      voiceId: voice.id,
      feature: this.props.feature,
    });
    this.setState({
      pickerState: this.state.pickerState
        ? {
            ...this.state.pickerState,
            selectedVoiceId: voice.id,
            selectedVoiceName: voice.displayName,
            selectedVoiceSource: "stored",
          }
        : undefined,
    });
  };

  /**
   * Row-click handler in the simplified picker — persists and previews.
   * Continue button calls persistBrowserFallback directly (no preview)
   * since the user is dismissing the modal, not exploring voices.
   */
  onPickBrowserFallback = (voice: VoiceOption) => {
    this.persistBrowserFallback(voice);
    // Preview in the entry feature's context so they hear what they'll get.
    this.previewVoice(voice.id, this.props.feature);
  };

  onPickRate = async (rate: number) => {
    await tts.setRate({
      user: stores.user,
      feature: this.props.feature,
      language: this.props.language,
      rate,
    });
    this.setState({
      pickerState: this.state.pickerState
        ? { ...this.state.pickerState, rate }
        : undefined,
    });
    const selectedVoiceId = this.state.pickerState?.selectedVoiceId;
    if (selectedVoiceId) this.previewVoice(selectedVoiceId, this.props.feature);
  };

  previewVoice = async (voiceId: string, feature: TTSFeature) => {
    await tts.previewVoice({
      user: stores.user,
      feature,
      language: this.props.language,
      voiceId,
      kind: "neutralSample",
    });
  };

  /** Selection-confirmation playback — "you have selected the voice X." */
  playSelectedConfirmation = async (voiceId: string) => {
    // Play the confirmation in the feature context where this voice will
    // actually work for the user (so Premium users picking HD hear the HD
    // voice, not the browser fallback that Read Aloud would use).
    await tts.previewVoice({
      user: stores.user,
      feature: "speakingMode",
      language: this.props.language,
      voiceId,
      kind: "voiceSelected",
    });
  };

  /**
   * Continue button. If the user opened the picker without ever picking and
   * the UI has been highlighting a "default" row (OS default browser voice
   * in the simplified view, server-default Azure voice in the full picker),
   * commit that highlighted row as their pick before closing — otherwise
   * the next speaker tap would either reopen the picker (no resolved voice)
   * or fall back to browser TTS (Azure default not yet persisted).
   *
   * Must await `setVoice` for the Azure path because the caller's close()
   * handler typically starts playback immediately (e.g. Read Aloud's
   * close → playTTS chain), and resolveVoice only sees the Azure voice
   * once the server PATCH response has been patched into the user store.
   */
  handleContinue = async () => {
    const pickerState =
      this.state.pickerState ??
      tts.getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language: this.props.language,
      });

    if (
      pickerState.selectedVoiceSource === "default" &&
      pickerState.selectedVoiceId
    ) {
      await tts.selectVoice({
        user: stores.user,
        language: this.props.language,
        voiceId: pickerState.selectedVoiceId,
        feature: this.props.feature,
      });
    }
    this.props.close();
  };

  openUpsell = () => {
    // modalDialog stacks natively — pushing the upsell hides the picker
    // until the user cancels back to it.
    modalController.show(
      <TTSUpsellModal
        language={this.props.language}
        feature={this.props.feature}
        close={() => modalController.cancel()}
      />
    );
  };

  languageName = () =>
    languageMap.map[this.props.language]?.english ?? this.props.language;

  render() {
    const { language } = this.props;
    const languageName = this.languageName();
    const pickerState = this.state.pickerState;
    const rate =
      pickerState?.rate ??
      tts.getRate({ user: stores.user, feature: this.props.feature, language });
    const mode =
      pickerState?.mode ??
      tts.getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language,
      }).mode;

    return (
      <div class="ttsVoicePicker">
        <h2 class="ttsPickerTitle">{languageName} voices</h2>
        <p class="ttsPickerLead">
          Voice used when reading the whole text, when Ami speaks a message
          aloud, when you tap a word or phrase, and during flashcard practice.
        </p>

        {mode === "simplified"
          ? this.renderSimplified(pickerState)
          : this.renderFullPicker(pickerState)}

        <TTSRateSelector rate={rate} onPickRate={this.onPickRate} />

        <button class="prominent continueButton" onClick={this.handleContinue}>
          Continue
        </button>
      </div>
    );
  }

  renderSimplified(pickerState?: VoicePickerState) {
    // The simplified view writes via setBrowserFallback so an existing HD
    // pref (e.g. Premium user's Dalia) stays in place for other features.
    // When the user hasn't picked yet, fall back to pickDefaultVoice so the
    // row that would actually play if they tapped a speaker icon is shown
    // as selected — otherwise the picker reads as "nothing selected" which
    // is confusing when the speaker icon path effectively *has* a default.
    const effectiveSelectedId = pickerState?.selectedVoiceId;
    const deviceGroup = pickerState?.groups.find(
      (group) => group.id === "device"
    );
    const deviceVoices = deviceGroup?.voices ?? [];
    const hdCount =
      pickerState?.upsellVoiceCount ??
      tts.getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language: this.props.language,
      }).upsellVoiceCount;

    return (
      <div class="ttsScrollArea">
        <ul class="ttsVoiceList">
          {!pickerState ? (
            <li class="ttsVoiceRowEmpty">Loading…</li>
          ) : deviceVoices.length === 0 ? (
            this.renderNoDeviceVoices()
          ) : (
            deviceVoices.map((voice) => (
              <TTSVoiceRow
                voice={voice}
                selected={effectiveSelectedId === voice.id}
                onSelect={() => this.onPickBrowserFallback(voice)}
                onPreview={() =>
                  this.previewVoice(voice.id, this.props.feature)
                }
              />
            ))
          )}
          {hdCount > 0 ? (
            <li class="ttsUpsellRow" onClick={this.openUpsell}>
              <span class="ttsUpsellStar">⭐</span>
              <span class="ttsUpsellLabel">
                Upgrade for {hdCount} high quality voice
                {hdCount === 1 ? "" : "s"}
              </span>
              <span class="ttsVoiceSpacer"></span>
              <span class="ttsUpsellChevron" aria-hidden="true">
                ›
              </span>
            </li>
          ) : null}
        </ul>
      </div>
    );
  }

  renderNoDeviceVoices() {
    return (
      <li class="ttsVoiceRowEmpty">
        This device has no {this.languageName()} voices.
      </li>
    );
  }

  renderFullPicker(pickerState?: VoicePickerState) {
    // For server-supported-but-uncurated languages (e.g. Bengali, Swahili)
    // there are no curated catalog entries, but the server can still
    // synthesize via the per-language default voice. Surface that default
    // as a single HD row so paid users get a working Azure pick instead of
    // an empty "No high quality voices available" screen.
    const fallbackState =
      pickerState ??
      tts.getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language: this.props.language,
      });
    const highQualityGroup = fallbackState.groups.find(
      (group) => group.id === "highQuality"
    );
    const deviceGroup = fallbackState.groups.find(
      (group) => group.id === "device"
    );
    const highQualityVoices = highQualityGroup?.voices ?? [];
    const deviceVoices = deviceGroup?.voices ?? [];
    const selectedVoiceId = fallbackState.selectedVoiceId;

    return (
      <div class="ttsScrollArea">
        <div class="ttsGroupHeader">
          <span>High quality voices</span>
        </div>
        <ul
          class={cn({ ttsVoiceList: true, disabled: highQualityGroup?.locked })}
        >
          {highQualityVoices.length === 0 ? (
            <li class="ttsVoiceRowEmpty">
              No high quality voices available for this language yet.
            </li>
          ) : (
            highQualityVoices.map((voice) => (
              <TTSVoiceRow
                voice={voice}
                selected={selectedVoiceId === voice.id}
                locked={highQualityGroup?.locked}
                onSelect={() => this.onPickVoice(voice)}
                onPreview={() =>
                  this.previewVoice(voice.id, this.props.feature)
                }
              />
            ))
          )}
        </ul>

        <div class="ttsGroupHeader">{deviceGroup?.label}</div>
        <ul class="ttsVoiceList">
          {!pickerState ? (
            <li class="ttsVoiceRowEmpty">Loading…</li>
          ) : deviceVoices.length === 0 ? (
            this.renderNoDeviceVoices()
          ) : (
            deviceVoices.map((voice) => (
              <TTSVoiceRow
                voice={voice}
                selected={selectedVoiceId === voice.id}
                onSelect={() => this.onPickVoice(voice)}
                onPreview={() =>
                  this.previewVoice(voice.id, this.props.feature)
                }
              />
            ))
          )}
        </ul>
      </div>
    );
  }
}

export default TTSVoicePickerView;

/** The app's prompt to pick an Auto-Pronounce voice (installed by app.ts) */
export const showSpeakingModeVoicePicker = (language: string): void => {
  modalController.show(
    <TTSVoicePickerView
      language={language}
      feature="speakingMode"
      close={() => modalController.cancelAll()}
    />
  );
};
